import React, { useState } from "react";
import { addIAMUser } from "@/services/firebase/functions";
import { useAuth } from "@/hooks/useAuth";

interface AddUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddUserDialog({ isOpen, onClose, onSuccess }: AddUserDialogProps) {
  const { user: currentUser } = useAuth();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "staff" | "developer" | "observer">("staff");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError("請輸入 email");
      return;
    }

    if (!currentUser?.isAdmin) {
      setError("只有管理員可以新增用戶");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await addIAMUser(email.trim(), role);
      if (result.success) {
        setEmail("");
        setRole("staff");
        // 等待一下讓 Functions 完成 Custom Claims 和 Firestore 的同步
        await new Promise(resolve => setTimeout(resolve, 1000));
        onSuccess();
        onClose();
      } else {
        setError("新增用戶失敗");
      }
    } catch (err: any) {
      setError(err.message || "新增用戶失敗");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail("");
      setRole("staff");
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-surface-800 rounded-xl shadow-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-surface-900 dark:text-white">
            新增 IAM 用戶
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-surface-500 hover:text-surface-700 dark:text-surface-400 dark:hover:text-surface-200 disabled:opacity-50"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              required
              disabled={loading}
              className="w-full px-4 py-2 border border-surface-300 dark:border-surface-600 rounded-lg bg-white dark:bg-surface-700 text-surface-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">
              用戶必須已經在系統中註冊
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              角色
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="role"
                  value="admin"
                  checked={role === "admin"}
                  onChange={() => setRole("admin")}
                  disabled={loading}
                  className="w-4 h-4 text-primary border-surface-300 focus:ring-primary"
                />
                <span className="text-sm text-surface-700 dark:text-surface-300">
                  管理員 (Admin)
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="role"
                  value="staff"
                  checked={role === "staff"}
                  onChange={() => setRole("staff")}
                  disabled={loading}
                  className="w-4 h-4 text-primary border-surface-300 focus:ring-primary"
                />
                <span className="text-sm text-surface-700 dark:text-surface-300">
                  工作人員 (Staff)
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="role"
                  value="developer"
                  checked={role === "developer"}
                  onChange={() => setRole("developer")}
                  disabled={loading}
                  className="w-4 h-4 text-primary border-surface-300 focus:ring-primary"
                />
                <span className="text-sm text-surface-700 dark:text-surface-300">
                  開發者 (Developer)
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="role"
                  value="observer"
                  checked={role === "observer"}
                  onChange={() => setRole("observer")}
                  disabled={loading}
                  className="w-4 h-4 text-primary border-surface-300 focus:ring-primary"
                />
                <span className="text-sm text-surface-700 dark:text-surface-300">
                  觀察者 (Observer)
                </span>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-surface-300 dark:border-surface-600 rounded-lg text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors disabled:opacity-50"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
            >
              {loading ? "處理中..." : "新增"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

